"use client";
import { facetAttributeNames, findAlgoliaResponse } from "@/apiUtils/algoliaUtils";
import {
  AlgoliaProductItem,
  AlgoliaProducts,
  AlgoliaResponse,
  AlgoliaSearchResult,
  FilteredFacet,
} from "@/apiUtils/algoliaUtils/types";
import { analyticEvents, handlePostAnalytics } from "@/apiUtils/analyticsUtils";
import { getCurrencyByLocale, hasArrayElements } from "@/apiUtils/commonUtils";
import {
  analyticEventSources,
  analyticEventTypes,
  pageTypes,
  plpLayoutConfig,
} from "@/apiUtils/constants";
import { getSchemaMarkUpsByPageType } from "@/apiUtils/schemaMarkupUtils";
import GridViewIcon from "@/assets/icons/grid-view.svg?react";
import SingleViewIcon from "@/assets/icons/single-view.svg?react";
import NoImageSrc from "@/assets/images/no_image.svg";
import {
  BannerType,
  BreadcrumbItem,
  CollectionPromotion,
  HeaderContent,
  HeaderDynamicBannerContent,
  NoResultsPageContent,
  PageBreakGridLayout,
  PageBreakPos,
  PlpContent,
  PopularBrandItem,
  PromotionTilesType,
  Range,
  SearchSection,
  Translation,
  VariantItem,
  VideoBannerType,
} from "@/common/lib/types";
import Filter from "@/components/filter";
import useDebounceCallback from "@/hooks/useDebounceCallback";
import useGridLayout from "@/hooks/useGridLayout";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import useTimeoutEffect from "@/hooks/useNudgeAnimationEffects";
import { cn } from "@/lib/tailwind";
import { getCloudinaryURL } from "@/utils/cloudinary";
import { debounce, getLangFromLocale } from "@/utils/common";
import {
  extractFilteredFacets,
  fetchAlgoliaData,
  getPageBreakLayout,
  getPopularBrands,
} from "@/utils/productList.utils";
import { createSignal } from "@/utils/signal";
import { DiscUnion } from "@/utils/type";
// import Image from "next/image";
import Image from "@/components/ui/imageV2/ImageV2";
import {
  Dispatch,
  Fragment,
  SetStateAction,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import HeaderVariants from "../common/headerVariants/HeaderVariants";
import HeaderEvents from "../HeaderEvents";
import BenefitSection from "../home/benefitSection/BenefitSection";
import Navbar from "../navbar/Navbar";
import CollectionPromotionBanner from "./CollectionPromotionBanner";
import ProductWithData from "../productcard/ProductWithData";
import { algoliaViewedObjectIDsListClient } from "@/apiUtils/algoliaUtils/indexClient";
import { eventParam } from "@/apiUtils/algoliaUtils/constants";
import { useGlobalStore } from "@/store/store";
import StructuredData from "../StructuredData";
import Breadcrumb, { BreadcrumbItem as BreadCrumbItem } from "../ui/Breadcrumb/Breadcrumb";
import { PaginationWithLinks } from "../ui/pagination/Pagination";
import PaginationInfo from "../ui/pagination/PaginationInfo";
import NoResults from "./NoResults";
import OverlayLoader from "./OverlayLoader";
import PlpBannerWidget from "./PlpBannerWidget";
import PlpEdits from "./PlpEdits";
import Skeleton from "./Skeleton";
import { useStableSearchParams } from "@/hooks/useStableSearchParams";
import PromoData from "./PromoData";
import SeoFooterText from "../common/SeoFooterText";

export interface IProps {
  productListingFilter: string;
  indexName: string;
  plpContent: PlpContent;
  lang: string;
  locale: string;
  searchParams: Record<string, string>;
  searchSection?: SearchSection;
  breadCrumbs?: BreadcrumbItem[];
  headerContent: HeaderContent;
  commonTranslations: Translation;
  isVtoEnabled: boolean;
  dynamicBanner?: HeaderDynamicBannerContent;
  dataNoResult?: NoResultsPageContent;
  sourceFromParent?: string;
  initialAlgoliaData?: {
    products: AlgoliaProductItem[];
    pagination: { page: number; totalPages: number; totalProducts: number };
    algoliaResponse: AlgoliaResponse[];
    mainResponse: AlgoliaSearchResult;
    allFacetsResponse: AlgoliaSearchResult;
  };
  collectionPromotions?: CollectionPromotion[];
}

export type PromoData = {
  [key: string]: {
    data: PromotionTilesType;
    span: string;
    pos: number;
    isFullRow: boolean;
    mobileHeight: number;
    desktopHeight: number;
    mobileWidth: number;
    desktopWidth: number;
    mobileAspectRatio: string;
    desktopAspectRatio: string;
  };
};

export type transitionStates = "shrinking" | "expanding";
export type transitionAction = "exit" | "enter";
export type EditTransitionState = `${transitionStates}-${transitionAction}` | "none";

export const productImageCarouselSignal = createSignal<Dispatch<SetStateAction<boolean>>[]>([]);

const OFFSET = 150;

const PLP = ({
  productListingFilter,
  indexName,
  lang,
  locale,
  plpContent,
  searchParams,
  searchSection,
  breadCrumbs,
  headerContent,
  commonTranslations,
  isVtoEnabled,
  dynamicBanner,
  dataNoResult,
  sourceFromParent,
  initialAlgoliaData,
  collectionPromotions,
}: IProps) => {
  // global store
  const { user } = useGlobalStore((state) => state);
  const hasInitialData = Boolean(initialAlgoliaData);

  const enableVirtualTryOn =
    headerContent?.enableVirtualTryOn !== null ? headerContent?.enableVirtualTryOn : true;
  const initialFilters = useMemo(
    () =>
      initialAlgoliaData
        ? extractFilteredFacets(
            plpContent.pageData?.configurations?.filters || [],
            initialAlgoliaData.mainResponse?.facets || [],
            initialAlgoliaData.algoliaResponse,
            enableVirtualTryOn,
          )
        : undefined,
    [initialAlgoliaData, plpContent.pageData?.configurations?.filters, enableVirtualTryOn],
  );
  const initialPriceRange = useMemo(
    () =>
      initialAlgoliaData?.allFacetsResponse?.rangeFacetData?.[facetAttributeNames?.price],
    [initialAlgoliaData],
  );

  // states
  const [isLoading, setIsLoading] = useState<boolean>(!hasInitialData);
  const [isOverlayLoading, setIsOverlayLoading] = useState<boolean>(!hasInitialData);
  const [products, setProducts] = useState<AlgoliaProductItem[]>(
    initialAlgoliaData?.products ?? [],
  );
  const [pagination, setPagination] = useState(
    initialAlgoliaData?.pagination ?? { page: 0, totalPages: 0, totalProducts: 0 },
  );
  const [filters, setFilters] = useState<FilteredFacet[] | undefined>(initialFilters);
  const [priceRange, setPriceRange] = useState<Range | undefined>(initialPriceRange);
  const [popularBrands, setPopularBrands] = useState<PopularBrandItem[]>([]);
  //const [isScrolling, setIsScrolling] = useState(false);

  //const lastScrollPos = useRef(OFFSET);

  const gaDataRef = useRef<AlgoliaProductItem[]>([]);

  // hooks
  const debounceCallback = useDebounceCallback(99);
  const firstTimeRef = useRef(true);
  const hasFetchedRef = useRef(false);
  const isSmallScreen = useMediaQuery("(max-width:768px)", {
    defaultValue: false,
    initializeWithValue: true,
  });
  const isMeidumScreen = useMediaQuery("(min-width: 768px) and (max-width: 1280px)", {
    defaultValue: false,
    initializeWithValue: true,
  });
  const isLargeScreen = useMediaQuery("(min-width: 1280px)", {
    defaultValue: false,
    initializeWithValue: true,
  });
  const { gridLayout, handleColumnView, handleGridView } = useGridLayout();

  // consts
  const schemaMarkups = getSchemaMarkUpsByPageType({
    pageType: pageTypes.PLP,
    breadCrumbs: breadCrumbs || [],
    locale,
  });

  const source =
    sourceFromParent ??
    `${analyticEventSources.PLP} - ${plpContent?.pageData?.name || plpContent?.pageData?.title}`;
  const sourceId = plpContent?.pageData?.id;
  const currency = getCurrencyByLocale(locale);
  const promoTiles = useMemo(
    () => plpContent?.pageData?.promotionTiles,
    [plpContent?.pageData?.promotionTiles],
  );

  const editsSection = useMemo(
    () => plpContent?.pageData?.editsSection,
    [plpContent?.pageData?.editsSection],
  );

  // const headerHasMedia = editsSection?.edits?.every((v) => Boolean(v.media));

  const sortFilters = useMemo(() => plpContent?.pageData?.configurations?.sorts || [], [plpContent]);
  const itemsPerPage = 36;
  const currentPagePromoTiles = useMemo(
    () => promoTiles?.items?.filter((tile) => tile.pageNumber === pagination.page) ?? [],
    [pagination, promoTiles],
  );

  const cols = useMemo(() => {
    if (isSmallScreen) return plpLayoutConfig.PRODUCTS_PER_ROW.SMALL;
    else if (isMeidumScreen) return plpLayoutConfig.PRODUCTS_PER_ROW.MEDIUM;
    else if (isLargeScreen) return plpLayoutConfig.PRODUCTS_PER_ROW.LARGE;
    return plpLayoutConfig.PRODUCTS_PER_ROW.SMALL;
  }, [isSmallScreen, isMeidumScreen, isLargeScreen]);

  const generatedProductsList = useMemo(() => {
    const promoTileObj: PromoData = {};
    const res: Array<
      | DiscUnion<"algolia", AlgoliaProductItem>
      | DiscUnion<
        "promo",
        {
          data: PromotionTilesType;
          span: string;
          pos: number;
          mobileHeight: number;
          desktopHeight: number;
          mobileWidth: number;
          desktopWidth: number;
          mobileAspectRatio: string;
          desktopAspectRatio: string;
        }
      >
    > = [];

    const sortedPromoTiles = [...currentPagePromoTiles].sort((a, b) => {
      const rowA = isSmallScreen ? a.row.mobile : a.row.desktop;
      const rowB = isSmallScreen ? b.row.mobile : b.row.desktop;
      return rowA - rowB;
    });

    for (const element of sortedPromoTiles) {
      if (!element.row) continue;
        const isMedia =
          element.type && element.position && element.contentType;
        const isCollections =
          element.tileVariant === "cta" &&
          Array.isArray(element.ctaTiles) &&
          element.ctaTiles.length > 0;
        if (!isMedia && !isCollections) continue;
      
        const layout = getPageBreakLayout({
          col: cols,
          size: (element.type ?? "fullWidth") as PageBreakPos["size"],
          position: (element.position ?? "left") as PageBreakPos["position"],
          row: isSmallScreen ? element.row.mobile : element.row.desktop,
        });

        const colSpanNum = parseInt(layout.colSpan.split("-")[2], 10) || 1;
        promoTileObj[layout.pos] = {
          data: element,
          span: layout.colSpan,
          pos: layout.pos,
          isFullRow: colSpanNum === cols,
          mobileHeight: layout?.mobileHeight,
          desktopHeight: layout?.desktopHeight,
          mobileWidth: layout.mobileWidth,
          desktopWidth: layout.desktopWidth,
          mobileAspectRatio: layout.mobileAspectRatio,
          desktopAspectRatio: layout.desktopAspectRatio,
        };
    }

    let gridIndex = 1; // This represents the actual grid position (1-based index)

    for (let i = 0; i < products.length; i++) {
      // Insert all promos at current gridIndex
      while (promoTileObj[gridIndex] !== undefined) {
        const promo = promoTileObj[gridIndex];
        res.push({ type: "promo", payload: promo });

        const span = parseInt(promo.span.split("-")[2]); // e.g., "col-span-2"
        gridIndex += span;
      }

      // Insert product at current gridIndex
      res.push({ type: "algolia", payload: products[i] });
      gridIndex += 1;
    }

    return res;
  }, [currentPagePromoTiles, cols, isSmallScreen, products]);

  // methods
  const debouncedAlgoliaResults = useCallback(
    debounce(() => getAlgoliaResults(), 100),
    [indexName, lang, productListingFilter, searchParams],
  );

  const handleAlgoliaData = (
    mainResponse: AlgoliaSearchResult,
    allFacetsResponse: AlgoliaSearchResult,
    algoliaResponse: AlgoliaResponse[],
  ) => {
    // console.log("🔍 [PLP handleAlgoliaData] mainResponse.nbHits:", mainResponse?.nbHits);
    // console.log("🔍 [PLP handleAlgoliaData] allFacetsResponse.nbHits:", allFacetsResponse?.nbHits);
    // console.log("🔍 [PLP handleAlgoliaData] mainResponse.hits.length:", mainResponse?.hits?.length);

    setProducts((mainResponse?.hits as AlgoliaProducts) || []); // list of products

    setPagination({
      page: mainResponse?.page || 1,
      totalPages: mainResponse?.nbPages || 1,
      totalProducts: mainResponse?.nbHits || 0,
    }); // used in paagination

    const priceQueryResponse = findAlgoliaResponse(algoliaResponse, facetAttributeNames?.price);
    setPriceRange(priceQueryResponse?.rangeFacetData?.[facetAttributeNames?.price]); // used in price range filter

    const extractFilters = extractFilteredFacets(
      plpContent?.pageData?.configurations?.filters || [],
      allFacetsResponse?.facets || {},
      algoliaResponse,
      enableVirtualTryOn,
    ); // extracted filter list from plp content and added facet mapping from algolia

    setFilters(extractFilters); // used to render filter and search queries

    // console.log("extractFilters", extractFilters)
    setIsLoading(false);
    setIsOverlayLoading(false);
  };

  const getAlgoliaResults = async () => {
    try {
      const { mainResponse, allFacetsResponse, algoliaResponse } = await fetchAlgoliaData(
        productListingFilter,
        indexName,
        searchParams || {},
        lang,
        plpContent?.pageData?.configurations?.filters || [],
      );

      handleAlgoliaData(mainResponse, allFacetsResponse, algoliaResponse);
    } catch (error) {
      setIsLoading(false);
      setIsOverlayLoading(false);
    }
  };

  // const toggleGridView = () => {
  //   setIsGridView((prev) => !prev);
  // };

  //   function handleDownScroll() {
  //     const curScrollPos = document.documentElement.scrollTop;
  //
  //     if (curScrollPos > lastScrollPos.current) {
  //       setIsScrolling(false);
  //     } else {
  //       setIsScrolling(true);
  //     }
  //
  //     lastScrollPos.current = curScrollPos <= OFFSET ? OFFSET : curScrollPos;
  //   }

  const triggerAnalyticsProductImpression = (visibleProductsData: AlgoliaProductItem[]) => {
    handlePostAnalytics({
      eventType: analyticEventTypes.DETAILS,
      data: {
        target: analyticEvents.VIEW_ITEM_LIST,
        products: visibleProductsData,
        currency,
        source,
        sourceId,
      },
    });

    const objectIDs = visibleProductsData.map((item) => item.objectID || (item as any).variantId);
    if (indexName && hasArrayElements(objectIDs)) {
      algoliaViewedObjectIDsListClient(
        indexName,
        eventParam.PRODUCT_VIEW,
        objectIDs,
        user?.magrabi_id,
      ).catch((e) => {
        console.error("Error while Algolia Product Impression Event : ", e);
      });
    }
  };

  const gaTriggerHandler = () => {
    triggerAnalyticsProductImpression(gaDataRef.current);
    gaDataRef.current = [];
  };

  const handleVisible = (isVisible: boolean, data: AlgoliaProductItem) => {
    if (isVisible) {
      gaDataRef.current.push(data);
      debounceCallback(gaTriggerHandler);
    }
  };

  const isNudgeVisible = useTimeoutEffect();

  const nudgeAnimationCard = (index: number, hit: AlgoliaProductItem | PromotionTilesType) => {
    const productItem = hit;
    const variantColorMappings = productItem?.payload?.variantColorMappings as VariantItem[];
    // TODO: Move to a common method
    const sortedVariants: VariantItem[] = Array.isArray(variantColorMappings)
      ? [...variantColorMappings].sort((a, b) => {
        // If a is in stock and b is not, a comes before b
        if (a.inStock && !b.inStock) {
          return -1;
        }
        // If b is in stock and a is not, b comes before a
        if (!a.inStock && b.inStock) {
          return 1;
        }
        // Otherwise, maintain the order
        return 0;
      })
      : [];

    const media = sortedVariants?.[0]?.media;
    if (!isNudgeVisible || !Array.isArray(media) || media?.length <= 1) {
      return false;
    }
    if (
      (gridLayout.view === "grid-view" && index === 1) ||
      (gridLayout.view === "column-view" && index === 0)
    ) {
      return true;
    }
    return false;
  };

  // effects

  useEffect(() => {
    getPopularBrands(plpContent?.pageData?.popularBrands?.brands || [])
      .then((brands) => setPopularBrands(brands))
      .catch((error) => console.error("Error while fetching popular brands : ", error));
  }, []);

  const breadcrumbItems: BreadCrumbItem[] | undefined = breadCrumbs
    ?.filter((v) => Boolean(v.label))
    .map((v) => ({
      label: v.label as string,
      path: v.href || undefined,
    }));

  const stableSearchParams = useStableSearchParams(searchParams);

  useEffect(() => {
    if (hasInitialData && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      return;
    }

    const handler = setTimeout(() => {
      if (firstTimeRef?.current) {
        firstTimeRef.current = false;
        setIsLoading(true);
      } else {
        setIsOverlayLoading(true);
      }
      debouncedAlgoliaResults();
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [indexName, lang, productListingFilter, stableSearchParams]);

  if (!isLoading && (!products || products?.length === 0 || !pagination?.totalProducts))
    return (
      <>
        <Suspense fallback={<></>}>
          <HeaderEvents pageType={pageTypes.PLP} pageCategory={plpContent?.pageData?.title || ""} />
        </Suspense>
        <Navbar
          headerContent={headerContent}
          translation={commonTranslations}
          enableVto={isVtoEnabled}
          dynamicBanner={dynamicBanner}
          socialMediaIcons={plpContent.socialMediaIcons ?? []}
        />
        <div
          id="breadcrumb-section"
          className="sticky z-[15] bg-white transition-[top] duration-200 ease-in-out"
        >
          {breadcrumbItems && breadcrumbItems.length > 0 && (
            <Breadcrumb className="px-3 md:px-12 py-2" items={breadcrumbItems} />
          )}
        </div>
        <NoResults
          data={searchSection}
          title={plpContent?.pageData?.title || ""}
          searchParam={searchParams?.q || searchParams?.cat || searchParams?.b || ""}
          benefits={plpContent.benefits}
          result={dataNoResult}
          desc={plpContent?.pageData?.description}
          translations={plpContent.translations}
          blockdata={plpContent}
          seoFooterText={plpContent?.pageData?.seoFooterText}
        />
      </>
    );

  return (
    <>
      {schemaMarkups?.length > 0 && <StructuredData schemaMarkups={schemaMarkups} />}
      <Suspense fallback={<></>}>
        <HeaderEvents pageType={pageTypes.PLP} pageCategory={plpContent?.pageData?.title || ""} />
      </Suspense>
      <Navbar
        headerContent={headerContent}
        translation={commonTranslations}
        enableVto={isVtoEnabled}
        dynamicBanner={dynamicBanner}
        socialMediaIcons={plpContent.socialMediaIcons ?? []}
      />
      <div
        id="breadcrumb-section"
        className="sticky z-[15] bg-white transition-[top] duration-200 ease-in-out"
      >
        {breadcrumbItems && breadcrumbItems.length > 0 && (
          <Breadcrumb className="px-3 md:px-12 py-2" items={breadcrumbItems} />
        )}
      </div>
        <CollectionPromotionBanner promotions={collectionPromotions} />
      <div>
          <section className="flex flex-col items-center gap-5">
            <h1 className="text-lg font-semibold md:text-[22px]">{plpContent?.pageData?.title}</h1>
            {/* {plpContent?.pageData?.description && (
              <HeaderVariants
                variant="withSubheading"
                subheading={plpContent?.pageData?.description}
                translations={plpContent?.translations}
              />
            )} */}

            {/* <div dangerouslySetInnerHTML={{__html: plpContent.pageData.description}}></div> */}
          </section>
          {(() => {
            const descriptionBelow =
              plpContent?.pageData?.descriptionPosition === "below";
            const descriptionBlock = plpContent.pageData.description ? (
              <section className="flex flex-col items-center gap-5">
                <HeaderVariants
                  variant="withSubheading"
                  subheading={plpContent.pageData.description}
                  translations={plpContent.translations}
                />
              </section>
            ) : null;
            const bannerBlock =
              products?.length !== 0 && plpContent?.pageData?.bannerSection?.type ? (
                <PlpBannerWidget
                  content={plpContent?.pageData?.bannerSection as BannerType | VideoBannerType}
                  locale={locale}
                />
              ) : null;
            return descriptionBelow ? (
              <>
                {bannerBlock}
                {descriptionBlock}
              </>
            ) : (
              <>
                {descriptionBlock}
                {bannerBlock}
              </>
            );
          })()}

          {editsSection?.edits?.length > 0 ? (
            <div
              id="plp-edit-section"
              className={cn(
                editsSection?.edits?.length > 0
                  ? `sticky z-10 overflow-hidden bg-white pb-6 pt-4 transition-[top] duration-200 ease-in-out md:pb-4 md:pt-6`
                  : "",
              )}
            >
              <PlpEdits editsSection={editsSection} locale={locale} />
            </div>
          ) : (
            <></>
          )}

          {isLoading ? (
            <div className="container px-4 md:px-12">
              <Skeleton />
            </div>
          ) : (
            <>
              <OverlayLoader isLoading={isOverlayLoading} />
              <div className="container relative md:px-12">
                {filters && (
                  <section
                    id="filter-section"
                    className={cn(
                      "sticky z-10 min-h-[60px] bg-white transition-[top] duration-200 ease-in-out",
                      editsSection?.edits?.length > 0 ? "mb-4 md:my-4" : "my-4",
                    )}
                  >
                    <Filter
                      locale={locale}
                      searchParams={searchParams}
                      totalProducts={pagination.totalProducts}
                      filters={filters}
                      sortFilters={sortFilters}
                      priceRange={priceRange}
                      popularBrands={{
                        title: plpContent?.pageData?.popularBrands?.title || "",
                        brands: popularBrands,
                      }}
                      translations={plpContent.translations}
                    />

                    <div className="my-4 flex items-center justify-between px-4 lg:hidden">
                      <p className="text-sm font-normal text-[#666666]">
                        {plpContent?.translations?.plp?.plp_product_count_multiple_label.replace(
                          "{{count}}",
                          `${pagination.totalProducts}`,
                        )}
                      </p>
                      <div className="flex md:hidden">
                        <button
                          onClick={() => {
                            // toggleGridView();
                            handleColumnView();
                          }}
                        >
                          <SingleViewIcon
                            className={cn(
                              gridLayout.view === "column-view" ? "text-black" : "text-tertiary-4",
                            )}
                          />
                        </button>
                        <button
                          onClick={() => {
                            // toggleGridView();
                            handleGridView();
                            // plpScrollSignal.setSignal([]);
                          }}
                        >
                          <GridViewIcon
                            className={cn(
                              gridLayout.view === "grid-view" ? "text-black" : "text-tertiary-4",
                            )}
                          />
                        </button>
                      </div>
                    </div>
                  </section>
                )}

                <section>
                  <div
                    className={cn(
                      gridLayout.view === "grid-view"
                        ? "grid-cols-2 gap-y-4"
                        : "grid-cols-1 gap-y-2",
                      "grid gap-x-[13px] px-4 md:grid-cols-3 md:gap-x-6 md:gap-y-2 md:px-0 md:pt-[1.32rem] xl:grid-cols-4",
                    )}
                  >
                    {generatedProductsList.map((prod, idx) => {
                      switch (prod.type) {
                        case "algolia":
                          return (
                            <ProductWithData
                              currency={currency}
                              key={`product-data-${idx}`}
                              product={prod.payload}
                              translations={plpContent?.translations}
                              isChevronVariant={
                                isSmallScreen ? !(gridLayout.view === "grid-view") : true
                              }
                              index={idx + 1}
                              handleVisible={handleVisible}
                              source={source}
                              sourceId={sourceId}
                              tryOn={isVtoEnabled}
                              isNudgeAnimationPresent={nudgeAnimationCard(idx, prod) ? true : false}
                            />
                          );
                        case "promo":
                          return (
                            <PromoData
                              key={`promo-data-${idx}`}
                              promo={prod.payload}
                              locale={locale}
                            />
                          );
                      }
                    })}
                  </div>
                </section>

                {pagination.totalPages > 1 && (
                  <section className="mt-28">
                    <PaginationInfo
                      page={pagination.page}
                      itemsPerPage={itemsPerPage}
                      totalPages={pagination.totalPages}
                      totalItems={pagination.totalProducts}
                      translation={
                        plpContent?.translations?.common?.common_pagination_details_label
                      }
                    />
                    <Suspense fallback={<></>}>
                      <PaginationWithLinks
                        lang={lang}
                        page={pagination.page}
                        pageSize={itemsPerPage}
                        totalCount={pagination.totalProducts}
                        translation={{
                          next:
                            plpContent?.translations?.common
                              ?.common_desktop_pagination_next_label || "",
                          previous:
                            plpContent?.translations?.common
                              ?.common_desktop_pagination_previous_label || "",
                        }}
                      />
                    </Suspense>
                  </section>
                )}
              </div>
            </>
          )}

          {plpContent?.pageData?.seoFooterText && (
            <section className="my-9">
              <SeoFooterText
                title={plpContent.pageData.seoFooterText.title}
                description={plpContent.pageData.seoFooterText.description}
                accordionContent={plpContent?.pageData?.seoFooterText.collapseSection}
                translations={plpContent?.translations}
              />
            </section>
          )}

          <section className="mt-28">
            <BenefitSection benefitsData={plpContent.benefits} />
          </section>
        </div>
    </>
  );
};

export default PLP;
